/**
 * 导入模块
 */
import { openWindow } from '../utils/index';

/**
 * 导入类型声明
 */
import type {
  SetCodeType,
  PopupWindowPosition,
  PopupWindowOptions,
  PopupTradeWindowOptions,
  PopupTradeWindowPosition,
  TradeWindowMoveOptions,
  MockTradeOrderOptions,
  OpenStockOptions
} from '../types/index';

/**
 * 是否在pc客户端内部
 * @returns
 */
export const isPcView = (): boolean => {
  return window.navigator.userAgent.includes('pc_hrhg');
}

/**
 * 根据market标识获取setcode
 * @param market
 * @returns
 */
export const getSetCodeByMarket = (market: string): SetCodeType => {
  let setcodeMap: Record<string, SetCodeType> = {
    SH: 1,
    SZ: 0,
    BJ: 13,
  };
  return setcodeMap[market];
};

/**
 * 封装本地存储
 * @param key
 * @param value
 */
export const setLocalStorage = <T>(key: string, value: T): void => {
  window.localStorage.setItem(key, JSON.stringify(value));
}

/**
 * 获取本地存储
 * @param key
 */
export const getLocalStorage = <T>(key: string): T | null => {
  let data = window.localStorage.getItem(key);
  if (!data || data === 'null') {
    return null;
  }
  try {
    let obj: T = JSON.parse(data);
    return obj;
  } catch (error) {
    return null;
  }
}

/**
 * 获取url后面参数
 * @param name 参数名
 * @returns 返回参数值，如果不存在则返回空字符串
 */
export const getQueryString = (name: string): string => {
  // 返回结果
  let result: string = '';
  // 匹配的字符串
  let matchString: string = '';

  let search: string = window.location.search;
  let hash: string = window.location.hash;

  if (search) {
    matchString = search;
  } else if (hash.includes('?')) {
    matchString = hash.substring(hash.indexOf('?'));
  }

  if (matchString) {
    let reg: RegExp | null = new RegExp('(^|&)' + name + '=([^&]*)(&|$)', 'i');
    let r: RegExpMatchArray | null = matchString.substring(1).match(reg);
    if (r) {
      result = r[2];
    }
  }
  return result;
}

/**
 * 打开客户端弹框方法
 * @param options
 * @returns
 */
export const popUpPage = (options: PopupWindowOptions): void => {
  if (!isPcView()) {
    let url: string = options.url || '';
    let width: number = options.width || 600;
    let height: number = options.height || 500;
    openWindow(url, width, height);
    return;
  }

  let url: string = options.url || '';
  let title: string = options.title || '';
  let width: number = options.width || 600;
  let height: number = options.height || 500;
  let position: PopupWindowPosition = options.position || 0;
  let channel: string = options.channel || 'ck';
  window.popWin(url, title, width, height, position, channel);
}

/**
 * 关闭分享弹窗
 * @param options
 * @returns
 */
export const closeWinDialog = (): void => {
  if (!isPcView()) {
    console.warn('关闭弹窗页');
    return;
  }
  window.closeWin();
}

/**
 * 打开模拟交易弹窗
 * @param options
 * @returns
 */
export const popTradePageWin = (options: PopupTradeWindowOptions): void => {
  if (!isPcView()) {
    console.warn('打开模拟交易弹窗');
    return;
  }
  let url: string = options.url || '';
  let title: string = options.title || '';
  let width: number = options.width || 600;
  let height: number = options.height || 500;
  let position: PopupTradeWindowPosition = options.position || 0;
  let dlgtype: number = options.dlgtype || 0;
  window.popTradeWin(url, title, width, height, position, '', dlgtype);
}

/**
 * 模拟交易交易成功
 * @param options
 * @returns
 */
export const noticeUpdateTrade = (): void => {
  if (!isPcView()) {
    console.warn('模拟交易交易成功通知其他页面');
    return;
  }
  window.updateTrade();
}

/**
 * 模拟交易窗体移动页面
 * @param options
 * @returns
 */
export const tradMoveTo = (options: TradeWindowMoveOptions): void => {
  if (!isPcView()) {
    console.warn('移动页面');
    return;
  }
  let width: number = options.width || 600;
  let height: number = options.height || 500;
  let position: PopupTradeWindowPosition = options.position || 0;
  window.moveWindowTo(width, height, position);
}

/**
 * 模拟交易登录成功通知其他页面
 * @param options
 * @returns
 */
export const noticeLoginTradeOk = (data: Record<string, any>): void => {
  if (!isPcView()) {
    console.warn('通知其他页面传参');
    return;
  }
  data ? window.loginTradeOk(JSON.stringify(data)) : window.loginTradeOk('');
}

/**
 * 模拟交易下单撤单
 * @param options
 * @returns
 */
export const confirmTradData = (options: MockTradeOrderOptions): boolean => {
  if (!isPcView()) {
    console.warn('股票交易');
    return false;
  }
  let type: number = options.type;
  let account: string = options.account || '';
  let code: string = options.code || '';
  let name: string = options.name || '';
  let price: number = options.price || 0;
  let vol: number = options.vol || 0;
  return window.confirm(JSON.stringify({ type, account, code, name, price, vol }));
}

/**
 * 打开股票详情
 * @param data 
 * @param rangList 
 * @param type 
 * @returns 
 */
export const openStockDetail = (data: OpenStockOptions, rangList: Array<OpenStockOptions> = [], type: 0 | 1 | -1): void => {
  if (!isPcView()) {
    console.warn('打开原生股票详情');
    return;
  }
  window.openStockView(JSON.stringify(data), type, JSON.stringify(rangList));
}

/**
 * 获取自选股
 * @param {*} callback
 */
export const getSelfStocks = (callback: (val: string) => void): void => {
  if (!isPcView()) {
    console.warn('原生获取自选股');
    return;
  }
  if (!callback) {
    console.error('未传入 callback');
    return;
  }
  window.getAllOptional(callback);
}

/**
 * 改变窗口大小
 * @param {*} width
 * @param {*} height
 * @returns
 */
export const resizeWindow = (width: number, height: number): void => {
  if (!isPcView()) {
    console.warn('原生改变window尺寸');
    return;
  }
  window.resizeWindow(width, height);
}

/**
 * 获取手机号
 * @param callback
 */
export const getMobile = (callback: (val: string) => void): void => {
  if (!isPcView()) {
    console.warn('原生获取登录手机号');
    return;
  }
  if (!callback) {
    console.error('未传入 callback');
    return;
  }
  window.getMobile(callback);
}

/**
 * 显示/隐藏窗口
 * @param state
 * @returns
 */
export const showWindow = (state: boolean): void => {
  if (!isPcView()) {
    console.warn('原生显示窗口');
    return;
  }
  window.showWindow(state);
}

/**
 * 是否已加入自选股
 * @param code
 * @param callback
 */
export const isOptional = (data: OpenStockOptions, callback: (val: boolean) => void): void => {
  if (!isPcView()) {
    console.warn('是否已加入自选股');
    return;
  }
  if (!callback) {
    console.error('未传入 callback');
    return;
  }
  window.isOptional(JSON.stringify(data), callback);
}

/**
 * 添加到自选股
 * @param code
 */
export const addToOptional = (data: OpenStockOptions): void => {
  if (!isPcView()) {
    console.warn('客户端添加到自选股');
    return;
  }
  window.addToOptional(JSON.stringify(data));
}

/**
 * 删除自选股
 * @param code
 * @returns
 */
export const delFromOptional = (data: OpenStockOptions): void => {
  if (!isPcView()) {
    console.warn('删除自选股');
    return;
  }
  window.delFromOptional(JSON.stringify(data));
}

/**
 *  加自选板块
 */
export const addOptionalPlate = (list: Array<OpenStockOptions> = []): void  =>{
  if (!isPcView()) {
    console.warn('打开原生加板块弹窗');
    return;
  }
  window.openBatchOperation(JSON.stringify(list));
}

/**
 * 获取自选股分组列表
 * @param callback
 * @returns
 */
export const getCustomBlockList = (callback: (val: string) => void): void => {
  if (!isPcView()) {
    console.warn('获取自选股分组列表');
    return;
  }
  if (!callback) {
    console.error('未传入 callback');
  }
  window.getCustomBlock(callback);
}

/**
 * 获取自选股分组股票列表
 * @param blockId
 * @param callback
 * @returns
 */
export const getCustomBlockStockList = (blockId: string, callback: (val: string) => void): void =>{
  if (!isPcView()) {
    console.warn('获取自选股分组股票列表');
    return;
  }
  if (!callback) {
    console.error('未传入 callback');
  }
  window.getStocksUserBlockId(blockId, callback);
}

/**
 * 退出客户端
 */
export const exitProcess = (msg: string, flag: boolean, cb?: () => void): void =>{
  if (!isPcView()) {
    if (cb) {
      cb();
    }
    return;
  }
  window.exitMain(msg, flag);
}

/**
 * 发送登录结果
 * @param val
 * @returns
 */
export const sendQrLoginResult = (val: string): void =>{
  if (!isPcView()) {
    console.warn('发送登录结果');
    return;
  }
  window.HrhgClientBridge.sendQrLoginResult(val);
}

/**
 * 打开IM
 */
export const openIM = (): void =>{
  if (!isPcView()) {
    console.warn('打开IM');
    return;
  }
  window.HrhgClientBridge.openIM();
}

/**
 * 退出登录
 */
export const logout = (type?: string, cb?: () => void): void =>{
  if (!isPcView()) {
    console.warn('退出登录');
    if (cb) {
      cb();
    }
    return;
  }
  if (type) {
    window.HrhgClientBridge.logout(type);
  } else {
    window.HrhgClientBridge.logout();
  }
}

/**
 * 通知客户端修改头像
 */
export const updateAvatar = (url: string): void =>{
  if (!isPcView()) {
    console.warn('通知客户端修改头像');
    return;
  }
  window.HrhgClientBridge.updateAvatar(url);
}

/**
 * 取消微信绑定
 */
export const cancelWechatBind = (): void => {
  if (!isPcView()) {
    console.warn('通知客户端关闭取消微信绑定弹框');
    return;
  }
  window.HrhgClientBridge.cancelWechatBind();
}

/**
 * 回到原生训练页面
 */
export const backToTrainingPage = (): void =>{
  if (!isPcView()) {
    console.warn('通知客户端回到原生训练页面');
    return;
  }
  window.HrhgClientBridge.backToTrainingPage();
}

/**
 * 打开指定的cmd模块
 * @param cmd
 * @param jsonParams
 * @returns
 */
export const execCommand = (cmd: string, data: string): void =>{
  if (!isPcView()) {
    console.warn('打开指定的cmd模块');
    return;
  }
  window.HrhgClientBridge.execCommand(cmd, data);
}

/**
 * 获取埋点属性
 */
export const getBuriedPointSuperProperties = (callback: (val: string) => void) =>{
  if (!isPcView()) {
    callback('');
    return;
  }
  window.getBuriedPointSuperProperties(callback);
}

